import axios from 'axios';
import { createAsyncThunk, createSlice, isFulfilled, isPending, isRejected } from '@reduxjs/toolkit';

import { serializeAxiosError } from 'app/shared/reducers/reducer.utils';
import { IDispenseCours, IDispenseCoursBatchRequest, defaultValue } from 'app/shared/model/dispense-cours.model';
import { IEleve } from 'app/shared/model/eleve.model';
import { IMatiereEcole } from 'app/shared/model/matiere.model';
import { ISousMatiere } from 'app/shared/model/sous-matiere.model';

const apiUrl = '/api/dispenses-cours';

export interface IDispenseCoursState {
  loading: boolean;
  errorMessage: string | null;
  entities: ReadonlyArray<IDispenseCours>;
  groupEntities: ReadonlyArray<IDispenseCours>;
  entity: IDispenseCours;
  updating: boolean;
  updateSuccess: boolean;
  groupLoading: boolean;
  groupRequestId: string | null;
  modalLoading: boolean;
  modalSousMatieresLoading: boolean;
  modalEleves: ReadonlyArray<IEleve>;
  modalMatieres: ReadonlyArray<IMatiereEcole>;
  modalSousMatieres: ReadonlyArray<ISousMatiere>;
}

const initialState: IDispenseCoursState = {
  loading: false,
  errorMessage: null,
  entities: [],
  groupEntities: [],
  entity: defaultValue,
  updating: false,
  updateSuccess: false,
  groupLoading: false,
  groupRequestId: null,
  modalLoading: false,
  modalSousMatieresLoading: false,
  modalEleves: [],
  modalMatieres: [],
  modalSousMatieres: [],
};

export const getDispensesCours = createAsyncThunk(
  'dispenseCours/fetch_list',
  async ({ inscriptionId, ecoleId, anneeScolaireId }: { inscriptionId?: string; ecoleId?: string; anneeScolaireId?: string }) => {
    const params = new URLSearchParams();
    if (inscriptionId) params.set('inscriptionId', inscriptionId);
    if (ecoleId) params.set('ecoleId', ecoleId);
    if (anneeScolaireId) params.set('anneeScolaireId', anneeScolaireId);

    return axios.get<IDispenseCours[]>(apiUrl, { params });
  },
  { serializeError: serializeAxiosError }
);

export const createDispenseCours = createAsyncThunk(
  'dispenseCours/create_entity',
  async (entity: Pick<IDispenseCours, 'inscriptionId' | 'matiereId' | 'sousMatiereId' | 'motif'>) => {
    return axios.post<IDispenseCours>(apiUrl, entity);
  },
  { serializeError: serializeAxiosError }
);

export const createDispensesCoursBatch = createAsyncThunk(
  'dispenseCours/create_batch',
  async (request: IDispenseCoursBatchRequest) => {
    return axios.post<IDispenseCours[]>(`${apiUrl}/batch`, request);
  },
  { serializeError: serializeAxiosError }
);

export const getDispensesCoursGroup = createAsyncThunk(
  'dispenseCours/fetch_group',
  async ({
    ecoleId,
    anneeScolaireId,
    classeId,
    matiereId,
    sousMatiereId,
  }: {
    ecoleId: string;
    anneeScolaireId: string;
    classeId: string;
    matiereId: string;
    sousMatiereId?: string;
  }) => {
    const params = new URLSearchParams();
    params.set('ecoleId', ecoleId);
    params.set('anneeScolaireId', anneeScolaireId);
    params.set('classeId', classeId);
    params.set('matiereId', matiereId);
    if (sousMatiereId) params.set('sousMatiereId', sousMatiereId);

    return axios.get<IDispenseCours[]>(`${apiUrl}/group`, { params });
  },
  { serializeError: serializeAxiosError }
);

export const deleteDispenseCours = createAsyncThunk(
  'dispenseCours/delete_entity',
  async (id: string) => {
    return axios.delete<IDispenseCours>(`${apiUrl}/${id}`);
  },
  { serializeError: serializeAxiosError }
);

export const getDispenseModalClasseData = createAsyncThunk(
  'dispenseCours/fetch_modal_classe_data',
  async ({ ecoleId, anneeScolaireId, classeId }: { ecoleId: string; anneeScolaireId: string; classeId: string }) => {
    const [elevesResponse, matieresResponse] = await Promise.all([
      axios.get<IEleve[]>('/api/eleves', {
        params: { ecoleId, anneeScolaireId, classe: classeId, page: 0, size: 500 },
      }),
      axios.get<IMatiereEcole[]>(`/api/matiere-ecoles/classe/${classeId}`),
    ]);

    return { eleves: elevesResponse.data || [], matieres: matieresResponse.data || [] };
  },
  { serializeError: serializeAxiosError }
);

export const getDispenseSousMatieres = createAsyncThunk(
  'dispenseCours/fetch_sous_matieres',
  async ({ cycleMatiereId, matiereEcoleId }: { cycleMatiereId?: string; matiereEcoleId?: string }) => {
    const params = new URLSearchParams();
    if (cycleMatiereId) params.set('cycleMatiereId', cycleMatiereId);
    if (matiereEcoleId) params.set('matiereEcoleId', matiereEcoleId);

    return axios.get<ISousMatiere[]>('/api/sous-matieres', { params });
  },
  { serializeError: serializeAxiosError }
);

export const DispenseCoursSlice = createSlice({
  name: 'dispenseCours',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    resetModalData(state) {
      state.modalLoading = false;
      state.modalSousMatieresLoading = false;
      state.modalEleves = [];
      state.modalMatieres = [];
      state.modalSousMatieres = [];
    },
    resetSousMatieres(state) {
      state.modalSousMatieresLoading = false;
      state.modalSousMatieres = [];
    },
    resetGroup(state) {
      state.groupLoading = false;
      state.groupRequestId = null;
      state.groupEntities = [];
    },
  },
  extraReducers(builder) {
    builder
      .addMatcher(isFulfilled(getDispensesCours), (state, action) => {
        state.loading = false;
        state.entities = action.payload.data || [];
      })
      .addMatcher(isFulfilled(getDispensesCoursGroup), (state, action) => {
        if (state.groupRequestId !== action.meta.requestId) {
          return;
        }
        state.groupLoading = false;
        state.groupRequestId = null;
        state.groupEntities = action.payload.data || [];
      })
      .addMatcher(isFulfilled(createDispenseCours), (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = action.payload.data;
      })
      .addMatcher(isFulfilled(createDispensesCoursBatch), (state, action) => {
        state.updating = false;
        state.updateSuccess = true;
        state.entity = action.payload.data?.[0] || defaultValue;
      })
      .addMatcher(isFulfilled(deleteDispenseCours), state => {
        state.updating = false;
        state.updateSuccess = true;
      })
      .addMatcher(isFulfilled(getDispenseModalClasseData), (state, action) => {
        state.modalLoading = false;
        state.modalEleves = action.payload.eleves;
        state.modalMatieres = action.payload.matieres;
      })
      .addMatcher(isFulfilled(getDispenseSousMatieres), (state, action) => {
        state.modalSousMatieresLoading = false;
        state.modalSousMatieres = action.payload.data || [];
      })
      .addMatcher(isPending(getDispensesCours), state => {
        state.errorMessage = null;
        state.loading = true;
      })
      .addMatcher(isPending(getDispensesCoursGroup), (state, action) => {
        state.errorMessage = null;
        state.groupLoading = true;
        state.groupRequestId = action.meta.requestId;
      })
      .addMatcher(isPending(createDispenseCours, createDispensesCoursBatch, deleteDispenseCours), state => {
        state.errorMessage = null;
        state.updateSuccess = false;
        state.updating = true;
      })
      .addMatcher(isPending(getDispenseModalClasseData), state => {
        state.errorMessage = null;
        state.modalLoading = true;
      })
      .addMatcher(isPending(getDispenseSousMatieres), state => {
        state.errorMessage = null;
        state.modalSousMatieresLoading = true;
      })
      .addMatcher(isRejected(getDispensesCours), (state, action) => {
        state.loading = false;
        state.errorMessage = action.error.message;
      })
      .addMatcher(isRejected(getDispensesCoursGroup), (state, action) => {
        if (state.groupRequestId !== action.meta.requestId) {
          return;
        }
        state.groupLoading = false;
        state.groupRequestId = null;
        state.errorMessage = action.error.message;
      })
      .addMatcher(isRejected(createDispenseCours, createDispensesCoursBatch, deleteDispenseCours), (state, action) => {
        state.updating = false;
        state.updateSuccess = false;
        state.errorMessage = action.error.message;
      })
      .addMatcher(isRejected(getDispenseModalClasseData), (state, action) => {
        state.modalLoading = false;
        state.errorMessage = action.error.message;
      })
      .addMatcher(isRejected(getDispenseSousMatieres), (state, action) => {
        state.modalSousMatieresLoading = false;
        state.errorMessage = action.error.message;
      });
  },
});

export const { reset, resetModalData, resetSousMatieres, resetGroup } = DispenseCoursSlice.actions;

export default DispenseCoursSlice.reducer;
